import * as yup from 'yup';
import { useTranslations } from 'hooks/useTranslations';

export const MAX_HOST_FINGERPRINTS = 10;

const useLicenceAuthorityPrintoutSettingsFormSchema = () => {
  const { t } = useTranslations();
  const sendHourSchema = yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError(t('general.field.validation.numberFormat.message'))
    .min(0, t('general.field.validation.minCharacters').replace('{0}', '0'))
    .max(23, t('general.field.validation.maxCharacters').replace('{0}', '23'));

  const schema = yup.object({
    username: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255'))
      .trim(),
    host: yup
      .string()
      .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255'))
      .required(t('general.field.validation.required')),
    port: yup
      .string()
      .max(10, t('general.field.validation.maxCharacters').replace('{0}', '10'))
      .required(t('general.field.validation.required')),
    hostFingerprints: yup
      .array()
      .of(
        yup
          .string()
          .required(t('general.field.validation.required'))
          .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255')),
      )
      .min(1, t('general.field.validation.required'))
      .max(MAX_HOST_FINGERPRINTS, t('general.field.validation.maxCharacters'))
      .required(t('general.field.validation.required')),
    pgpPublicKey: yup.string().required(t('general.field.validation.required')),
    outboundPath: yup.string().required(t('general.field.validation.required')),
    inboundPath: yup.string().required(t('general.field.validation.required')),
    sendFrequency: yup
      .string()
      .oneOf(['onceADay', 'twiceADay'])
      .required(t('general.field.validation.required')),
    sendHours: yup
      .array()
      .of(sendHourSchema)
      .required(t('general.field.validation.required'))
      .max(2, t('general.field.validation.maxItems').replace('{0}', '2'))
      .test('first-send-hour-required', function (value) {
        if (value?.[0] !== undefined && value[0] !== null) return true;

        return this.createError({
          path: `${this.path}[0]`,
          message: t('general.field.validation.required'),
        });
      })
      .test('second-send-hour-required', function (value) {
        const secondHourIsRequired = this.parent.sendFrequency === 'twiceADay';
        const secondHourIsSelected = value?.[1] !== undefined && value[1] !== null;

        if (!secondHourIsRequired || secondHourIsSelected) return true;

        return this.createError({
          path: `${this.path}[1]`,
          message: t('general.field.validation.required'),
        });
      })
      .test('min-three-hours-diff', function (value) {
        if (!value || value.length < 2) return true;

        const [first, second] = value;

        if (first === undefined || second === undefined) return true;

        if (Math.abs(first - second) >= 3) return true;

        return this.createError({
          path: `${this.path}[1]`,
          message: t(
            'licenceAuthoritiesPrintoutSettings.sendHours.validation.minThreeHoursDiff',
          ),
        });
      }),
  });

  return { schema };
};

export default useLicenceAuthorityPrintoutSettingsFormSchema;
